export interface TypingAnimationOptions {
	text: string
	typingSpeed?: number // base milliseconds per character (default: 15)
	onUpdate: (displayedText: string) => void
	onComplete?: () => void
}

export interface TypingAnimationController {
	start: () => void
	skip: () => void
	updateText: (newFullText: string) => void
	isTyping: () => boolean
	cleanup: () => void
}

export function createTypingAnimation(options: TypingAnimationOptions): TypingAnimationController {
	let { text, typingSpeed = 15, onUpdate, onComplete } = options

	const imageRegex = /!\[.*?\]\((.*?)\)/g
	let segments: { type: 'text' | 'image'; content: string }[] = []

	const parseSegments = (currentText: string) => {
		const newSegments: { type: 'text' | 'image'; content: string }[] = []
		let lastIndex = 0
		for (const match of currentText.matchAll(imageRegex)) {
			if (match.index! > lastIndex) {
				newSegments.push({
					type: 'text',
					content: currentText.slice(lastIndex, match.index)
				})
			}
			newSegments.push({
				type: 'image',
				content: match[0]
			})
			lastIndex = match.index! + match[0].length
		}
		if (lastIndex < currentText.length) {
			newSegments.push({
				type: 'text',
				content: currentText.slice(lastIndex)
			})
		}
		return newSegments
	}

	segments = parseSegments(text)

	let currentSegmentIndex = 0
	let currentCharIndex = 0
	let currentOutput = ''
	let timeoutId: ReturnType<typeof setTimeout> | null = null
	let isActive = false

	const typeNextChar = () => {
		if (currentSegmentIndex >= segments.length) {
			isActive = false
			timeoutId = null
			onComplete?.()
			return
		}

		const segment = segments[currentSegmentIndex]

		// Instantly append image segments
		if (segment.type === 'image') {
			currentOutput += segment.content
			onUpdate(currentOutput)
			currentSegmentIndex++
			currentCharIndex = 0
			typeNextChar()
			return
		}

		// Gradually type text segments
		if (currentCharIndex < segment.content.length) {
			currentOutput += segment.content[currentCharIndex++]
			onUpdate(currentOutput)

			const remainingCharsInTotal = text.length - currentOutput.length
			const currentDelay = remainingCharsInTotal > 50 ? Math.max(2, typingSpeed / 4) : typingSpeed

			timeoutId = setTimeout(typeNextChar, currentDelay)
		} else {
			// Move to next segment
			currentSegmentIndex++
			currentCharIndex = 0
			typeNextChar()
		}
	}

	return {
		start: () => {
			if (isActive) return
			isActive = true
			typeNextChar()
		},

		skip: () => {
			if (timeoutId) {
				clearTimeout(timeoutId)
				timeoutId = null
			}
			isActive = false
			currentOutput = text
			onUpdate(text)
			onComplete?.()
		},

		updateText: (newFullText: string) => {
			if (text === newFullText) return

			text = newFullText
			segments = parseSegments(text)

			if (isActive) return

			if (timeoutId) {
				clearTimeout(timeoutId)
				timeoutId = null
			}

			if (text.length <= currentOutput.length) {
				currentOutput = text
				onUpdate(currentOutput)
				onComplete?.()
				return
			}

			let offset = 0

			for (let i = 0; i < segments.length; i++) {
				const length = segments[i].content.length
				const nextOffset = offset + length

				if (nextOffset > currentOutput.length) {
					currentSegmentIndex = i
					currentCharIndex = currentOutput.length - offset
					break
				}

				offset = nextOffset
			}

			isActive = true
			typeNextChar()
		},

		isTyping: () => isActive,

		cleanup: () => {
			if (timeoutId) {
				clearTimeout(timeoutId)
				timeoutId = null
			}
			isActive = false
		}
	}
}
